import { useEffect, useRef, useState } from 'react';

interface WalletBonusPopupProps {
  isOpen: boolean;
  earnedBonus: number;
  newWalletTotal: number;
  paidAmount: number;
  onClose: () => void;
}

export default function WalletBonusPopup({
  isOpen,
  earnedBonus,
  newWalletTotal,
  paidAmount,
  onClose,
}: WalletBonusPopupProps) {
  const [animStep, setAnimStep] = useState(0);
  const [countdown, setCountdown] = useState(5);
  // Keep latest onClose in ref so interval doesn't go stale
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      setAnimStep(0);
      setCountdown(5);
      return;
    }

    // Staggered animation steps
    const t1 = setTimeout(() => setAnimStep(1), 80);
    const t2 = setTimeout(() => setAnimStep(2), 500);
    const t3 = setTimeout(() => setAnimStep(3), 1000);

    // Countdown auto-close
    let rem = 5;
    const interval = setInterval(() => {
      rem -= 1;
      setCountdown(rem);
      if (rem <= 0) {
        clearInterval(interval);
        onCloseRef.current();
      }
    }, 1000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearInterval(interval);
    };
  // Only re-run when isOpen changes — NOT when onClose changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      {/* Blurred overlay */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />

      {/* Floating coins animation */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-float-coin"
            style={{
              left: `${8 + i * 8}%`,
              bottom: '-10%',
              animationDelay: `${i * 0.18}s`,
              animationDuration: `${2.2 + (i % 4) * 0.4}s`,
            }}
          >
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-lg"
              style={{
                background:
                  i % 3 === 0
                    ? 'linear-gradient(135deg,#fbbf24,#f59e0b)'
                    : i % 3 === 1
                    ? 'linear-gradient(135deg,#34d399,#10b981)'
                    : 'linear-gradient(135deg,#a78bfa,#8b5cf6)',
                color: '#fff',
              }}
            >
              ₹
            </div>
          </div>
        ))}
      </div>

      {/* Main card */}
      <div
        className="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl"
        style={{
          background: 'linear-gradient(160deg,#0f172a 0%,#1e293b 100%)',
          border: '1px solid rgba(255,255,255,0.12)',
          transform: animStep >= 1 ? 'scale(1) translateY(0)' : 'scale(0.85) translateY(40px)',
          opacity: animStep >= 1 ? 1 : 0,
          transition: 'transform 0.5s cubic-bezier(.34,1.56,.64,1), opacity 0.4s ease',
        }}
      >
        {/* Top gradient strip */}
        <div
          className="h-1 w-full"
          style={{ background: 'linear-gradient(90deg,#10b981,#059669,#34d399)' }}
        />

        <div className="p-7 text-center">
          {/* Check circle */}
          <div
            className="mx-auto mb-4 relative"
            style={{
              width: 80,
              height: 80,
              transform: animStep >= 1 ? 'scale(1)' : 'scale(0)',
              transition: 'transform 0.55s cubic-bezier(.34,1.56,.64,1) 0.1s',
            }}
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}
            >
              <i className="ri-check-line text-white text-4xl font-bold" />
            </div>
            <div
              className="absolute inset-0 rounded-full animate-ping"
              style={{ background: 'rgba(16,185,129,0.25)' }}
            />
          </div>

          {/* Order Confirmed */}
          <div
            style={{
              opacity: animStep >= 1 ? 1 : 0,
              transform: animStep >= 1 ? 'translateY(0)' : 'translateY(16px)',
              transition: 'all 0.4s ease 0.3s',
            }}
          >
            <p className="text-green-400 text-xs font-bold tracking-widest uppercase mb-1">
              Order Confirmed
            </p>
            <h2 className="text-white text-2xl font-bold mb-1">Payment: ₹{paidAmount}</h2>
            <p className="text-gray-400 text-sm">Cash on Delivery · Order Placed</p>
          </div>

          {/* Divider */}
          <div className="my-5 h-px w-full" style={{ background: 'rgba(255,255,255,0.08)' }} />

          {/* Wallet Bonus Earned */}
          <div
            className="rounded-2xl p-4 mb-4"
            style={{
              background: 'linear-gradient(135deg,rgba(16,185,129,0.15),rgba(5,150,105,0.1))',
              border: '1px solid rgba(16,185,129,0.3)',
              opacity: animStep >= 2 ? 1 : 0,
              transform: animStep >= 2 ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
              transition: 'all 0.5s cubic-bezier(.34,1.56,.64,1) 0.15s',
            }}
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <i className="ri-wallet-3-fill text-green-400 text-xl" />
              <span className="text-green-300 text-sm font-semibold">Wallet Bonus Earned!</span>
            </div>
            <div
              className="text-5xl font-black mb-1"
              style={{
                background: 'linear-gradient(135deg,#34d399,#10b981)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              +₹{earnedBonus}
            </div>
            <p className="text-gray-400 text-xs">10% cashback on ₹{paidAmount} paid</p>
          </div>

          {/* New wallet total */}
          <div
            className="rounded-xl px-4 py-3 mb-5 flex items-center justify-between"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              opacity: animStep >= 3 ? 1 : 0,
              transform: animStep >= 3 ? 'translateY(0)' : 'translateY(12px)',
              transition: 'all 0.4s ease 0.1s',
            }}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: 'rgba(245,158,11,0.2)' }}
              >
                <i className="ri-coin-fill text-amber-400 text-base" />
              </div>
              <div className="text-left">
                <p className="text-gray-400 text-xs">Total Wallet Balance</p>
                <p className="text-white font-bold text-base">₹{newWalletTotal}</p>
              </div>
            </div>
            <div
              className="text-right"
              style={{
                background: 'linear-gradient(135deg,rgba(245,158,11,0.15),rgba(251,191,36,0.1))',
                border: '1px solid rgba(245,158,11,0.25)',
                borderRadius: 10,
                padding: '4px 10px',
              }}
            >
              <p className="text-amber-400 text-xs font-bold whitespace-nowrap">Next order mein</p>
              <p className="text-amber-300 text-xs whitespace-nowrap">₹{newWalletTotal} minus hoga!</p>
            </div>
          </div>

          {/* Info tip */}
          <div
            className="rounded-xl px-3 py-2.5 mb-5 flex items-start gap-2 text-left"
            style={{
              background: 'rgba(139,92,246,0.1)',
              border: '1px solid rgba(139,92,246,0.2)',
              opacity: animStep >= 3 ? 1 : 0,
              transition: 'opacity 0.4s ease 0.3s',
            }}
          >
            <i className="ri-information-line text-purple-400 text-sm mt-0.5 shrink-0" />
            <p className="text-purple-300 text-xs leading-relaxed">
              Cart mein{' '}
              <span className="font-bold text-purple-200">"Wallet Balance Apply"</span> karo — ₹
              {newWalletTotal} automatically deduct ho jaega!
            </p>
          </div>

          {/* Auto-close countdown button */}
          <button
            onClick={() => onCloseRef.current()}
            className="w-full py-3.5 rounded-2xl font-bold text-sm cursor-pointer whitespace-nowrap transition-all hover:opacity-90"
            style={{
              background: 'linear-gradient(135deg,#10b981,#059669)',
              color: '#fff',
              opacity: animStep >= 2 ? 1 : 0,
              transition: 'opacity 0.3s ease 0.5s',
            }}
          >
            <i className="ri-arrow-right-line mr-1" />
            View My Orders ({countdown}s)
          </button>
        </div>
      </div>

      <style>{`
        @keyframes floatCoin {
          0%   { transform: translateY(0) rotate(0deg); opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(-110vh) rotate(720deg); opacity: 0; }
        }
        .animate-float-coin {
          animation: floatCoin linear infinite;
        }
      `}</style>
    </div>
  );
}
